import clsx from 'clsx';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type HTMLAttributes,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import { PageContext } from './contexts/PageContext.js';
import { DetailView } from './DetailView.js';
import { Header } from './Header.js';
import { Main } from './Main.js';
import { pageWrapperCSS } from './Page.sty.js';
import { ParentPanel } from './Panel/ParentPanel.js';
import type { PanelSize } from './Panel/types/shared-panel-types.js';
import { PanelControlButton } from './PanelControlButton.js';
import { Sidebar } from './Sidebar.js';
import { pageStateReducer } from './state/page-state-reducer.js';
import type {
  PagePanels,
  DismissiblePagePanels,
} from './types/shared-page-types.js';
import { usePrevious } from '../../core/hooks/usePrevious.js';

//#region constants
/**
 * @internal
 */
export const MIN_WIDTHS: Record<PagePanels, number> = {
  sidebar: 200,
  main: 320,
  details: 320,
};

/**
 * @internal
 */
export const BREAKPOINTS: Record<Exclude<PagePanels, 'main'>, number> = {
  sidebar: 640,
  details: 1280,
};

/**
 * @internal
 */
export const PREFERRED_WIDTHS: Partial<Record<PagePanels, PanelSize>> = {
  sidebar: '15%',
  details: '25%',
};

/**
 * @internal
 */
export const PANEL_ORDER: Record<PagePanels, number> = {
  sidebar: 0,
  main: 1,
  details: 2,
};
//#endregion

//#region types & interfaces

/**
 * @public
 */
export interface PageProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, keyof StylingProps>,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Whether animations should be disabled.
   * If true or 'prefers-reduced-motion' is set to 'reduce', no animations are applied.
   * @defaultValue false
   */
  disableAnimations?: boolean;
}
//#endregion

const PageRoot = (props: PageProps) => {
  const {
    disableAnimations = false,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: PageProps = props;
  const [state, dispatch] = useReducer(pageStateReducer, {
    sidebar: { controlled: false, breakpoint: -1 },
    details: { controlled: false, breakpoint: -1 },
  });
  const prevState = usePrevious(state);

  const [detailsDismissed, setDetailsDismissed] = useState(true);
  const [sidebarDismissed, setSidebarDismissed] = useState(true);

  // Callback included in the context to set the dismissed state of a side panel.
  const setPanelDismissed = useCallback(
    (name: DismissiblePagePanels, value: boolean) => {
      switch (name) {
        case 'details':
          setDetailsDismissed(value);
          break;
        case 'sidebar':
          setSidebarDismissed(value);
          break;
      }
    },
    [],
  );

  // Callback included in the context to get the dismissed state of a side panel.
  const isPanelDismissed = useCallback(
    (name: DismissiblePagePanels) => {
      switch (name) {
        case 'details':
          return detailsDismissed;
        case 'sidebar':
          return sidebarDismissed;
      }
    },
    [detailsDismissed, sidebarDismissed],
  );

  /** Update the dismissed state of an uncontrolled side panel if the opposing panel is controlled. */
  const updateUncontrolledPagePanel = useCallback(
    (controlledPanel: DismissiblePagePanels) => {
      const { drawer } = state[controlledPanel];
      const oppositePanel: DismissiblePagePanels =
        controlledPanel === 'details' ? 'sidebar' : 'details';

      // Panel is uncontrolled and has been opened by the user.
      if (
        !state[oppositePanel].controlled &&
        state[oppositePanel].userAction === 'open'
      ) {
        // Both panels would be rendered as drawer -> dismiss panel.
        // If there's enough space again, open the panel again.
        if (
          drawer &&
          !isPanelDismissed(controlledPanel) &&
          state[oppositePanel].drawer === true
        ) {
          setPanelDismissed(oppositePanel, true);
        } else {
          setPanelDismissed(oppositePanel, false);
        }
      }
    },
    [isPanelDismissed, setPanelDismissed, state],
  );

  const memoizedContext = useMemo(
    () => ({
      state,
      dispatch,
      detailsDismissed,
      sidebarDismissed,
      setPanelDismissed,
      isPanelDismissed,
    }),
    [
      detailsDismissed,
      isPanelDismissed,
      setPanelDismissed,
      sidebarDismissed,
      state,
    ],
  );

  // Effect handling opening / closing of controlled / uncontrolled panels.
  useEffect(() => {
    for (const [_name, panelState] of Object.entries(state)) {
      const name = _name as DismissiblePagePanels;
      const { controlled, defaultDismissed, drawer, breakpoint, userAction } =
        panelState;

      // Handle controlled panels.
      if (controlled) {
        switch (name) {
          case 'details':
            updateUncontrolledPagePanel(name);
            break;
          case 'sidebar': {
            updateUncontrolledPagePanel(name);
            break;
          }
        }

        continue;
      }

      // Handle uncontrolled panels.

      // Keep panel closed if it was closed by the user.
      if (userAction === 'close') {
        setPanelDismissed(name, true);
        continue;
      }

      // If there was no user interaction yet and the panel was not dismissed by
      // default, we set the dismissed state according to the breakpoint.
      if (userAction === undefined && defaultDismissed === false) {
        setPanelDismissed(name, !!drawer);
        continue;
      }

      // If the panel was opened by the user, the state depends on the currently
      // opened panels.
      if (state[name].userAction === 'open') {
        switch (name) {
          case 'details':
            // The detail view should always be the more important panel, so
            // we need to handle things differently depending on the breakpoints.
            if (state.sidebar.breakpoint < breakpoint) {
              // Both side panels are uncontrolled.
              if (!state.sidebar.controlled) {
                // Both panels would be rendered as drawer -> dismiss sidebar.
                if (state.sidebar.drawer && drawer && !detailsDismissed) {
                  setPanelDismissed('sidebar', true);
                  // There's enough space again to render the sidebar as normal panel
                  // -> reopen the sidebar if it was opened by the user before.
                  // Only reopen after initialization.
                } else if (
                  prevState?.sidebar.userAction !== undefined &&
                  state.sidebar.userAction === 'open' &&
                  prevState?.sidebar.userAction === state.sidebar.userAction
                ) {
                  setPanelDismissed('sidebar', false);
                }
              }
            } else if (state.sidebar.drawer) {
              // Detail view is rendered as normal panel.
              if (!drawer) {
                setPanelDismissed(name, false);

                if (state.sidebar.userAction === 'open') {
                  setPanelDismissed('sidebar', false);
                }
                // Both panels would be rendered as drawer -> dismiss sidebar.
              } else if (!state.sidebar.controlled) {
                setPanelDismissed('sidebar', true);
              }
            }
            break;
          case 'sidebar':
            // Intentionally left empty.
            // Since we loop through all side panels whenever the state changes,
            // checking only one panel is enough to ensure the proper panels are open.
            break;
        }
      }
    }
    // The effect should only run when the state is updated to update opened panels accordingly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <div
      className={clsx(pageWrapperCSS, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingProps}
    >
      <PageContext.Provider value={memoizedContext}>
        <ParentPanel
          data-testid="parent-panel"
          name="page-root"
          disableAnimations={disableAnimations}
        >
          {children}
        </ParentPanel>
      </PageContext.Provider>
    </div>
  );
};

/**
 * The `Page` component provides the basic layout for your app with slots for a header, sidebar, main content, and a detail view.
 * The `Main` slot is the only slot that is mandatory for rendering the `Page`; every other slot is optional.
 * @public
 */
export const Page = Object.assign(PageRoot, {
  /** The `Header` component is used inside the `Page` component for displaying content at the very top of the page. */
  Header,
  /** The `Sidebar` component is used inside the `Page` component for displaying content in the corresponding panel. */
  Sidebar,
  /**
   * The `Main` component is used inside the `Page` component for displaying content in the corresponding panel.
   * It is the only compound that is mandatory for rendering the `Page`.
   */
  Main,
  /** The `DetailView` component is used inside the `Page` component for displaying content in the corresponding panel. */
  DetailView,
  /**
   * Use the `PanelControlButton` to get the correct icon for toggling the sidebar / detail view.
   * Depending on where it is rendered and the current state of the panel, the corresponding icon is rendered.
   * You can customize the rendered content by passing any children.
   */
  PanelControlButton,
});
